<script lang="js" setup>
import { ref, onMounted, computed } from "vue";
import * as XLSX from 'xlsx';
import { useToast } from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-default.css';

// Add formatDate function
function formatDate(dateString) {
    if (!dateString) return '-';
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '-';
        
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        
        // Convert to 12-hour format with AM/PM
        let hours = date.getHours();
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12;
        hours = hours ? hours : 12; // the hour '0' should be '12'
        const formattedHours = String(hours).padStart(2, '0');
        
        return `${day}/${month}/${year} ${formattedHours}:${minutes} ${ampm}`;
    } catch (error) {
        console.error('Error formatting date:', error);
        return '-';
    }
}

// Format date without time
function formatDateOnly(dateString) {
    if (!dateString) return '-';
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '-';
        
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        
        return `${day}/${month}/${year}`;
    } catch (error) {
        console.error('Error formatting date:', error);
        return '-';
    }
}

const allData = ref([]);
const elementsPerPage = ref(25);
const currentPage = ref(1);
const filterQuery = ref("");
const toast = useToast();
const showEmployeeModal = ref(false);
const selectedUniformItems = ref([]);
const showSubCategoryModal = ref(false);
const selectedSubCategories = ref([]);
const showPenaltyModal = ref(false);
const penaltyData = ref({
    amount: null,
    reason: ""
});
const selectedRequestId = ref(null);

const getAuthToken = () => localStorage.getItem('token');
const baseUrl = process.env.VUE_APP_BASE_URL;

onMounted(async () => {
    try {
        const token = getAuthToken();
        const response = await fetch(`${baseUrl}api/operation/materialRequest/getAll`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        });

        const data = await response.json();
        console.log('API Response:', data);

        if (data && data.success && data.data) {
            allData.value = data.data;
            console.log('Data loaded successfully:', allData.value);
            if (allData.value.length === 0) {
                toast.warning('No records found');
            }
        } else {
            console.log('Invalid response structure:', data);
            toast.error('Invalid data format received');
        }
    } catch (error) {
        console.error('API Error:', error);
        toast.error('Error fetching data');
    }
});

// ... pagination functions remain the same ...

// Add these refs for individual filters
const filters = ref({
    reqDate: '',
    site: '',
    manager: '',
    departments: '',
    expenseType: '',
    category: '',
    uniformItems: '',
    employees: '',
    preferredDate: '',
    urgentReason: '',
    status: ''
});

const filteredData = computed(() => {
    const data = Array.isArray(allData.value) ? allData.value : [];
    
    const filtered = data.filter(item => {
        // Helper function to safely check if a string includes the query
        const includes = (str, query) => String(str || '').toLowerCase().includes(query.toLowerCase());
        
        // Check each filter
        const matchesReqDate = !filters.value.reqDate || 
            (item.reqDate && formatDate(item.reqDate).includes(filters.value.reqDate));
            
        const matchesSite = !filters.value.site || 
            includes(item.siteId?.siteName, filters.value.site);
            
        const matchesManager = !filters.value.manager || 
            includes(item.reqManagerId?.employeeName, filters.value.manager);
            
        const matchesDepartments = !filters.value.departments || 
            item.departments?.some(dept => includes(dept.name, filters.value.departments));
            
        const matchesExpenseType = !filters.value.expenseType || 
            includes(item.expenseType, filters.value.expenseType);
            
        const matchesCategory = !filters.value.category || 
            includes(item.category, filters.value.category);
            
        const matchesUniformItems = !filters.value.uniformItems || 
            item.uniformItems?.some(uniform => 
                includes(uniform.itemName, filters.value.uniformItems) || 
                includes(uniform.department, filters.value.uniformItems) ||
                includes(uniform.size, filters.value.uniformItems)
            );
            
        const matchesEmployees = !filters.value.employees || 
            item.employeeIds?.some(emp => includes(emp.employeeName, filters.value.employees));
            
        const matchesPreferredDate = !filters.value.preferredDate || 
            (item.preferredDate && formatDate(item.preferredDate).includes(filters.value.preferredDate));
            
        const matchesUrgentReason = !filters.value.urgentReason || 
            includes(item.urgentReason, filters.value.urgentReason);
            
        const matchesStatus = !filters.value.status || 
            includes(item.status, filters.value.status);
        
        return matchesReqDate && matchesSite && matchesManager && matchesDepartments && 
               matchesExpenseType && matchesCategory && matchesUniformItems && 
               matchesEmployees && matchesPreferredDate && matchesUrgentReason && matchesStatus;
    });
    
    return filtered.slice(
        (currentPage.value - 1) * elementsPerPage.value,
        currentPage.value * elementsPerPage.value
    );
});

function downloadExcel() {
    const formattedData = allData.value.map(item => ({
        'Request Date': formatDate(item.reqDate),
        'Site': item.siteId?.siteName || '-',
        'Manager': item.reqManagerId?.employeeName || '-',
        'Departments': item.departments?.map(dept => dept.name).join(', ') || '-',
        'Expense Type': item.expenseType || '-',
        'Category': item.category || '-',
        'Uniform Items': item.uniformItems?.map(uniform => 
            `${uniform.itemName} (${uniform.department}, Size: ${uniform.size}, Qty: ${uniform.quantity}, Employees: ${uniform.employeeIds?.map(e => e.employeeName).join(', ') || 'None'})`
        ).join('; ') || '-',
        'Uploaded Picture': item.uploadedPic || '-',
        'Preferred Date': formatDate(item.preferredDate),
        'Urgent Reason': item.urgentReason || '-',
        'Status': item.status || '-',
        'Created At': formatDate(item.createdAt),
        'Updated At': formatDate(item.updatedAt)
    }));

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Material Requests");
    XLSX.writeFile(workbook, 'material-requests.xlsx');
}

function num_pages() {
    const query = filterQuery.value.toLowerCase().trim();
    const data = Array.isArray(allData.value) ? allData.value : [];
    
    if (!query) return Math.ceil(data.length / elementsPerPage.value);
    
    const filtered = data.filter(item => {
        const includes = (str) => String(str || '').toLowerCase().includes(query);
        
        return (
            includes(item.category) ||
            includes(item.status) ||
            includes(item.expenseType) ||
            includes(item.urgentReason) ||
            (item.reqDate && new Date(item.reqDate).toLocaleDateString().includes(query)) ||
            (item.preferredDate && new Date(item.preferredDate).toLocaleDateString().includes(query)) ||
            includes(item.siteId?.siteName) ||
            includes(item.reqManagerId?.employeeName) ||
            item.departments?.some(dept => includes(dept.name)) ||
            item.employeeIds?.some(emp => includes(emp.employeeName)) ||
            item.uniformItems?.some(uniform => 
                includes(uniform.itemName) ||
                includes(uniform.department) ||
                includes(uniform.size)
            )
        );
    });
    
    return Math.ceil(filtered.length / elementsPerPage.value);
}

function change_page(page) {
    currentPage.value = page;
}

function previousPage() {
    if (currentPage.value > 1) {
        currentPage.value--;
    }
}

function nextPage() {
    if (currentPage.value < num_pages()) {
        currentPage.value++;
    }
}

async function acceptRequest(id) {
    try {
        const token = getAuthToken();
        const response = await fetch(`${baseUrl}api/operation/materialRequest/accept/${id}`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        });

        const data = await response.json();
        
        if (data.success) {
            toast.success('Material request accepted successfully');
            // Refresh the data
            const updatedItem = allData.value.find(item => item._id === id);
            if (updatedItem) {
                updatedItem.status = 'Approved';
            }
        } else {
            toast.error(data.message || 'Failed to accept request');
        }
    } catch (error) {
        console.error('Error accepting request:', error);
        toast.error('Error accepting request');
    }
}

async function rejectRequest(id) {
    try {
        const token = getAuthToken();
        const response = await fetch(`${baseUrl}api/operation/materialRequest/reject/${id}`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        });

        const data = await response.json();
        
        if (data.success) {
            toast.success('Material request rejected successfully');
            // Refresh the data
            const updatedItem = allData.value.find(item => item._id === id);
            if (updatedItem) {
                updatedItem.status = 'Rejected';
            }
        } else {
            toast.error(data.message || 'Failed to reject request');
        }
    } catch (error) {
        console.error('Error rejecting request:', error);
        toast.error('Error rejecting request');
    }
}

function openPenaltyModal(id) {
    selectedRequestId.value = id;
    penaltyData.value = {
        amount: null,
        reason: ""
    };
    showPenaltyModal.value = true;
}

function closePenaltyModal() {
    showPenaltyModal.value = false;
    selectedRequestId.value = null;
}

async function submitPenalty() {
    try {
        const token = getAuthToken();
        
        // Prepare payload, only include reason if it's not empty
        const payload = {
            amount: penaltyData.value.amount
        };
        
        if (penaltyData.value.reason && penaltyData.value.reason.trim()) {
            payload.reason = penaltyData.value.reason;
        }
        
        const response = await fetch(`${baseUrl}api/operation/materialRequest/penalty/add/${selectedRequestId.value}`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(payload)
        });

        const data = await response.json();
        
        if (data.success) {
            toast.success('Penalty added successfully');
            closePenaltyModal();
            // Optionally refresh the data or update the UI
        } else {
            toast.error(data.message || 'Failed to add penalty');
        }
    } catch (error) {
        console.error('Error adding penalty:', error);
        toast.error('Error adding penalty');
    }
}

// Add this function to clear all filters
function clearFilters() {
    filters.value = {
        reqDate: '',
        site: '',
        manager: '',
        departments: '',
        expenseType: '',
        category: '',
        uniformItems: '',
        employees: '',
        preferredDate: '',
        urgentReason: '',
        status: ''
    };
}

// Function to get all employees from uniform items
function getAllEmployees(uniformItems) {
    const employees = [];
    uniformItems?.forEach(uniform => {
        uniform.employeeIds?.forEach(emp => {
            employees.push({
                ...emp,
                itemName: uniform.itemName,
                size: uniform.size,
                department: uniform.department
            });
        });
    });
    return employees;
}

// Function to get total employee count
function getTotalEmployeeCount(uniformItems) {
    return getAllEmployees(uniformItems).length;
}

// Function to open employee modal
function openEmployeeModal(uniformItems) {
    selectedUniformItems.value = uniformItems;
    showEmployeeModal.value = true;
}

// Function to close employee modal
function closeEmployeeModal() {
    showEmployeeModal.value = false;
    selectedUniformItems.value = [];
}

// Function to open sub category modal
function openSubCategoryModal(uniformItems) {
    selectedSubCategories.value = uniformItems;
    showSubCategoryModal.value = true;
}

// Function to close sub category modal
function closeSubCategoryModal() {
    showSubCategoryModal.value = false;
    selectedSubCategories.value = [];
}
</script>

<template>
    <Breadcrumbs main="Apps" title="Material Request" />
    <div class="container-fluid">
        <div class="email-wrap bookmark-wrap">
            <div class="row card card-body">
                <div class="table-responsive product-table custom-scrollbar">
                    <form class="no-footer" @submit.prevent>
                        <!-- Search and Download buttons -->
                

                        <!-- Filter section -->
                        <div class="mb-3">
                            <div class="row g-3">
                                <!-- Request Date Filter -->
                                <div class="col-md-2">
                                    <label class="form-label">Request Date</label>
                                    <input type="date" class="form-control" v-model="filters.reqDate">
                                </div>
                                
                                <!-- Site Filter -->
                                <div class="col-md-2">
                                    <label class="form-label">Site</label>
                                    <input type="text" class="form-control" v-model="filters.site" placeholder="Filter site...">
                                </div>
                                
                                <!-- Manager Filter -->
                                <div class="col-md-2">
                                    <label class="form-label">Manager</label>
                                    <input type="text" class="form-control" v-model="filters.manager" placeholder="Filter manager...">
                                </div>

                                <!-- Department Filter -->
                                <div class="col-md-2">
                                    <label class="form-label">Department</label>
                                    <input type="text" class="form-control" v-model="filters.departments" placeholder="Filter department...">
                                </div>

                                <!-- Expense Type Filter -->
                                <div class="col-md-2">
                                    <label class="form-label">Expense Type</label>
                                    <select class="form-select" v-model="filters.expenseType">
                                        <option value="">All</option>
                                        <option value="Reimbursable">Reimbursable</option>
                                        <option value="Non-Reimbursable">Non-Reimbursable</option>
                                    </select>
                                </div>

                                <!-- Category Filter -->
                                <div class="col-md-2">
                                    <label class="form-label">Category</label>
                                    <select class="form-select" v-model="filters.category">
                                        <option value="">All</option>
                                        <option value="Uniform">Uniform</option>
                                        <option value="Housekeeping Chemicals">Housekeeping Chemicals</option>
                                    </select>
                                </div>

                                <!-- Uniform Items Filter -->
                                <div class="col-md-2">
                                    <label class="form-label">Uniform Items</label>
                                    <input type="text" class="form-control" v-model="filters.uniformItems" placeholder="Filter uniform items...">
                                </div>

                                <!-- Employees Filter -->
                                <div class="col-md-2">
                                    <label class="form-label">Employees</label>
                                    <input type="text" class="form-control" v-model="filters.employees" placeholder="Filter employees...">
                                </div>

                                <!-- Preferred Date Filter -->
                                <div class="col-md-2">
                                    <label class="form-label">Preferred Date</label>
                                    <input type="date" class="form-control" v-model="filters.preferredDate">
                                </div>

                                <!-- Status Filter -->
                                <div class="col-md-2">
                                    <label class="form-label">Status</label>
                                    <select class="form-select" v-model="filters.status">
                                        <option value="">All</option>
                                        <option value="Pending">Pending</option>
                                        <option value="Approved">Approved</option>
                                        <option value="Rejected">Rejected</option>
                                    </select>
                                </div>
                                
                                <!-- Clear Filters Button -->
                                <div class="col-md-2 d-flex align-items-end">
                                    <button type="button" class="btn btn-secondary" @click="clearFilters">
                                        Clear Filters
                                    </button>
                                </div>
                            </div>
                        </div>

                        <!-- Table -->
                        <table class="display table-striped table-hover table-bordered table" id="basic-1">
                            <thead>
                                <tr>
                                    <th class="sorting_asc" scope="col">SL</th>
                                    <th class="sorting_asc" scope="col">Request Date</th>
                                    <th class="sorting_asc" scope="col">Site</th>
                                    <th class="sorting_asc" scope="col">Manager</th>
                                    <th class="sorting_asc" scope="col">Departments</th>
                                    <th class="sorting_asc" scope="col">Expense Type</th>
                                    <th class="sorting_asc" scope="col">Category</th>
                                    <th class="sorting_asc" scope="col">Sub Categories</th>
                                    <th class="sorting_asc" scope="col">Employees</th>
                                    <th class="sorting_asc" scope="col">Uploaded Pic</th>
                                    <th class="sorting_asc" scope="col">Preferred Date</th>
                                    <th class="sorting_asc" scope="col">Urgent Reason</th>
                                    <th class="sorting_asc" scope="col">Status</th>
                                    <th class="sorting_asc" scope="col">Action</th>
                                </tr>
                            </thead>
                            <tbody v-if="!filteredData.length">
                                <tr class="odd">
                                    <td valign="top" colspan="14" class="dataTables_empty">
                                        No matching records found
                                        <br>
                                        <small class="text-muted">Debug: Total records: {{ allData.length }}</small>
                                    </td>
                                </tr>
                            </tbody>
                            <tbody v-else>
                                <tr v-for="(item, index) in filteredData" :key="item._id" 
                                    :class="{ 'urgent-row': item.urgentReason }">
                                    <td>{{ index + 1 }}</td>
                                    <td>{{ formatDate(item.reqDate) }}</td>
                                    <td>{{ item.siteId?.siteName || '-' }}</td>
                                    <td>{{ item.reqManagerId?.employeeName || '-' }}</td>
                                    <td>
                                        <span v-for="(dept, idx) in item.departments" :key="dept._id">
                                            {{ dept.name }}{{ idx < item.departments.length - 1 ? ', ' : '' }}
                                        </span>
                                    </td>
                                    <td>{{ item.expenseType || '-' }}</td>
                                    <td>{{ item.category || '-' }}</td>
                                    <td>
                                        <div v-if="item.uniformItems?.length">
                                            <div v-for="(uniform, idx) in item.uniformItems.slice(0, 1)" :key="idx" class="mb-2">
                                                <div class="small">
                                                    <strong>{{ uniform.itemName }}</strong>
                                                    <br>
                                                    <small class="text-muted">
                                                        Department: {{ uniform.department }}<br>
                                                        Size: {{ uniform.size }}<br>
                                                        Quantity: {{ uniform.quantity }}
                                                    </small>
                                                </div>
                                            </div>
                                            <button 
                                                v-if="item.uniformItems.length > 1" 
                                                class="btn btn-link btn-sm p-0 mt-1"
                                                @click="openSubCategoryModal(item.uniformItems)">
                                                View {{ item.uniformItems.length - 1 }} more...
                                            </button>
                                        </div>
                                        <span v-else>-</span>
                                    </td>
                                    <td>
                                        <div v-if="item.uniformItems?.length" class="employees">
                                            <div v-if="getAllEmployees(item.uniformItems).length > 0">
                                                <div v-for="(emp, empIdx) in getAllEmployees(item.uniformItems).slice(0, 1)" :key="empIdx">
                                                    <small>
                                                        {{ empIdx + 1 }}. {{ emp.employeeName }}
                                                        <span class="text-muted">({{ emp.itemName }} - {{ emp.size }})</span>
                                                    </small>
                                                </div>
                                                <button 
                                                    v-if="getTotalEmployeeCount(item.uniformItems) > 1" 
                                                    class="btn btn-link btn-sm p-0 mt-1"
                                                    @click="openEmployeeModal(item.uniformItems)">
                                                    View {{ getTotalEmployeeCount(item.uniformItems) - 1 }} more...
                                                </button>
                                            </div>
                                            <span v-else class="text-muted">No employees</span>
                                        </div>
                                        <span v-else>-</span>
                                    </td>
                                    <td>
                                        <a v-if="item.uploadedPic" :href="item.uploadedPic" target="_blank" class="btn btn-sm btn-info">
                                            View Image
                                        </a>
                                        <span v-else>-</span>
                                    </td>
                                    <td>{{ formatDateOnly(item.preferredDate) }}</td>
                                    <td>{{ item.urgentReason || '-' }}</td>
                                    <td>
                                        <span :class="{
                                            'badge rounded-pill bg-success': item.status === 'Approved',
                                            'badge rounded-pill bg-warning': item.status === 'Pending',
                                            'badge rounded-pill bg-danger': item.status === 'Rejected'
                                        }">{{ item.status || 'Unknown' }}</span>
                                    </td>
                                    <td>
                                        <div v-if="item.status === 'Pending'" class="d-flex gap-1 flex-wrap align-items-center">
                                            <button class="btn btn-primary" style="font-size: 0.7rem; padding: 0.2rem 0.4rem;" @click="acceptRequest(item._id)">Accept</button>
                                            <button class="btn btn-danger" style="font-size: 0.7rem; padding: 0.2rem 0.4rem;" @click="rejectRequest(item._id)">Reject</button>
                                            <button 
                                                v-if="item.urgentReason && !item.penalty?.isApplicable" 
                                                class="btn btn-warning" 
                                                style="font-size: 0.7rem; padding: 0.2rem 0.4rem;"
                                                @click="openPenaltyModal(item._id)">
                                                Add Penalty
                                            </button>
                                            <div v-if="item.penalty?.isApplicable" class="d-flex flex-column gap-1">
                                                <span 
                                                    :class="{
                                                        'badge rounded-pill bg-success': item.penalty.status === 'Accepted',
                                                        'badge rounded-pill bg-danger': item.penalty.status !== 'Accepted'
                                                    }"
                                                    style="font-size: 0.7rem;">
                                                    Penalty: ₹{{ item.penalty.amount }} ({{ item.penalty.status }})
                                                </span>
                                                <small v-if="item.penalty.status === 'Rejected' && item.penalty.rejectionReason" class="text-danger" style="font-size: 0.65rem;">
                                                    Reason: {{ item.penalty.rejectionReason }}
                                                </small>
                                                <small v-if="item.penalty.rejectedDate" class="text-muted" style="font-size: 0.65rem;">
                                                    Rejected: {{ formatDate(item.penalty.rejectedDate) }}
                                                </small>
                                                <small v-if="item.penalty.acceptedDate" class="text-success" style="font-size: 0.65rem;">
                                                    Accepted: {{ formatDate(item.penalty.acceptedDate) }}
                                                </small>
                                            </div>
                                        </div>
                                        <div v-else-if="item.urgentReason && (item.status === 'Approved' || item.status === 'Rejected')" class="d-flex gap-1 align-items-center">
                                            <button 
                                                v-if="!item.penalty?.isApplicable"
                                                class="btn btn-warning" 
                                                style="font-size: 0.7rem; padding: 0.2rem 0.4rem;"
                                                @click="openPenaltyModal(item._id)">
                                                Add Penalty
                                            </button>
                                            <div v-if="item.penalty?.isApplicable" class="d-flex flex-column gap-1">
                                                <span 
                                                    :class="{
                                                        'badge rounded-pill bg-success': item.penalty.status === 'Accepted',
                                                        'badge rounded-pill bg-danger': item.penalty.status !== 'Accepted'
                                                    }"
                                                    style="font-size: 0.7rem;">
                                                    Penalty: ₹{{ item.penalty.amount }} ({{ item.penalty.status }})
                                                </span>
                                                <small v-if="item.penalty.status === 'Rejected' && item.penalty.rejectionReason" class="text-danger" style="font-size: 0.65rem;">
                                                    Reason: {{ item.penalty.rejectionReason }}
                                                </small>
                                                <small v-if="item.penalty.rejectedDate" class="text-muted" style="font-size: 0.65rem;">
                                                    Rejected: {{ formatDate(item.penalty.rejectedDate) }}
                                                </small>
                                                <small v-if="item.penalty.acceptedDate" class="text-success" style="font-size: 0.65rem;">
                                                    Accepted: {{ formatDate(item.penalty.acceptedDate) }}
                                                </small>
                                            </div>
                                        </div>
                                        <span v-else>-</span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>

                        <!-- Pagination -->
                        <ul class="pagination py-2 justify-content-end pagination-primary">
                            <li class="page-item" @click="previousPage">
                                <a class="page-link">Previous</a>
                            </li>
                            <li class="page-item" 
                                v-for="i in num_pages()" 
                                :key="i" 
                                :class="[i == currentPage ? 'active' : '']" 
                                @click="change_page(i)">
                                <a class="page-link">{{ i }}</a>
                            </li>
                            <li class="page-item" @click="nextPage">
                                <a class="page-link">Next</a>
                            </li>
                        </ul>
                    </form>
                </div>
            </div>
        </div>
    </div>

    <!-- Employee Modal -->
    <div v-if="showEmployeeModal" class="modal fade show" style="display: block; background: rgba(0,0,0,0.5);" @click.self="closeEmployeeModal">
        <div class="modal-dialog modal-dialog-centered modal-lg">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title">Employee Details</h5>
                    <button type="button" class="btn-close" @click="closeEmployeeModal"></button>
                </div>
                <div class="modal-body" style="max-height: 500px; overflow-y: auto;">
                    <table class="table table-bordered table-striped" style="border: 1.5px solid #dee2e6;">
                        <thead>
                            <tr style="background-color: #f8f9fa;">
                                <th style="border: 1.5px solid #dee2e6; font-weight: 600;">SL</th>
                                <th style="border: 1.5px solid #dee2e6; font-weight: 600;">Item Name</th>
                                <th style="border: 1.5px solid #dee2e6; font-weight: 600;">Size</th>
                                <th style="border: 1.5px solid #dee2e6; font-weight: 600;">Department</th>
                                <th style="border: 1.5px solid #dee2e6; font-weight: 600;">Quantity</th>
                                <th style="border: 1.5px solid #dee2e6; font-weight: 600;">Employee Name</th>
                            </tr>
                        </thead>
                        <tbody>
                            <template v-for="(uniform, idx) in selectedUniformItems" :key="idx">
                                <tr v-if="!uniform.employeeIds?.length">
                                    <td style="border: 1.5px solid #dee2e6;">{{ idx + 1 }}</td>
                                    <td style="border: 1.5px solid #dee2e6;">{{ uniform.itemName }}</td>
                                    <td style="border: 1.5px solid #dee2e6;">{{ uniform.size }}</td>
                                    <td style="border: 1.5px solid #dee2e6;">{{ uniform.department }}</td>
                                    <td style="border: 1.5px solid #dee2e6;">{{ uniform.quantity }}</td>
                                    <td class="text-muted" style="border: 1.5px solid #dee2e6;">No employees</td>
                                </tr>
                                <tr v-else v-for="(emp, empIdx) in uniform.employeeIds" :key="emp._id">
                                    <td v-if="empIdx === 0" :rowspan="uniform.employeeIds.length" style="border: 2px solid #6c757d; background-color: #f8f9fa; font-weight: 500; vertical-align: middle;">{{ idx + 1 }}</td>
                                    <td v-if="empIdx === 0" :rowspan="uniform.employeeIds.length" style="border: 2px solid #6c757d; background-color: #f8f9fa; font-weight: 500; vertical-align: middle;">{{ uniform.itemName }}</td>
                                    <td v-if="empIdx === 0" :rowspan="uniform.employeeIds.length" style="border: 2px solid #6c757d; background-color: #f8f9fa; font-weight: 500; vertical-align: middle;">{{ uniform.size }}</td>
                                    <td v-if="empIdx === 0" :rowspan="uniform.employeeIds.length" style="border: 2px solid #6c757d; background-color: #f8f9fa; font-weight: 500; vertical-align: middle;">{{ uniform.department }}</td>
                                    <td v-if="empIdx === 0" :rowspan="uniform.employeeIds.length" style="border: 2px solid #6c757d; background-color: #f8f9fa; font-weight: 500; vertical-align: middle;">{{ uniform.quantity }}</td>
                                    <td style="border: 2px solid #6c757d;">{{ emp.employeeName }}</td>
                                </tr>
                            </template>
                        </tbody>
                    </table>
                </div>
                <div class="modal-footer">
                    <div class="me-auto">
                        <strong>Total Employees: {{ getTotalEmployeeCount(selectedUniformItems) }}</strong>
                    </div>
                    <button type="button" class="btn btn-secondary" @click="closeEmployeeModal">Close</button>
                </div>
            </div>
        </div>
    </div>

    <!-- Sub Category Modal -->
    <div v-if="showSubCategoryModal" class="modal fade show" style="display: block; background: rgba(0,0,0,0.5);" @click.self="closeSubCategoryModal">
        <div class="modal-dialog modal-dialog-centered modal-lg">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title">Sub Categories Details</h5>
                    <button type="button" class="btn-close" @click="closeSubCategoryModal"></button>
                </div>
                <div class="modal-body" style="max-height: 500px; overflow-y: auto;">
                    <table class="table table-bordered table-striped" style="border: 1.5px solid #dee2e6;">
                        <thead>
                            <tr style="background-color: #f8f9fa;">
                                <th style="border: 1.5px solid #dee2e6; font-weight: 600;">SL</th>
                                <th style="border: 1.5px solid #dee2e6; font-weight: 600;">Item Name</th>
                                <th style="border: 1.5px solid #dee2e6; font-weight: 600;">Department</th>
                                <th style="border: 1.5px solid #dee2e6; font-weight: 600;">Size</th>
                                <th style="border: 1.5px solid #dee2e6; font-weight: 600;">Quantity</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(uniform, idx) in selectedSubCategories" :key="idx">
                                <td style="border: 2px solid #6c757d;">{{ idx + 1 }}</td>
                                <td style="border: 2px solid #6c757d; font-weight: 500;">{{ uniform.itemName }}</td>
                                <td style="border: 2px solid #6c757d;">{{ uniform.department }}</td>
                                <td style="border: 2px solid #6c757d;">{{ uniform.size }}</td>
                                <td style="border: 2px solid #6c757d;">{{ uniform.quantity }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div class="modal-footer">
                    <div class="me-auto">
                        <strong>Total Items: {{ selectedSubCategories.length }}</strong>
                    </div>
                    <button type="button" class="btn btn-secondary" @click="closeSubCategoryModal">Close</button>
                </div>
            </div>
        </div>
    </div>

    <!-- Penalty Modal -->
    <div v-if="showPenaltyModal" class="modal fade show" style="display: block; background: rgba(0,0,0,0.5);" @click.self="closePenaltyModal">
        <div class="modal-dialog modal-dialog-centered">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title">Add Penalty</h5>
                    <button type="button" class="btn-close" @click="closePenaltyModal"></button>
                </div>
                <div class="modal-body">
                    <div class="mb-3">
                        <label for="penaltyAmount" class="form-label">Amount</label>
                        <input 
                            type="number" 
                            class="form-control" 
                            id="penaltyAmount" 
                            v-model="penaltyData.amount"
                            placeholder="Enter penalty amount">
                    </div>
                    <div class="mb-3">
                        <label for="penaltyReason" class="form-label">Reason <span class="text-muted">(Optional)</span></label>
                        <textarea 
                            class="form-control" 
                            id="penaltyReason" 
                            rows="4"
                            v-model="penaltyData.reason"
                            placeholder="Enter penalty reason (optional)"></textarea>
                    </div>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-secondary" @click="closePenaltyModal">Cancel</button>
                    <button type="button" class="btn btn-primary" @click="submitPenalty">Submit Penalty</button>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
/* Urgent row styling with higher specificity */
tbody tr.urgent-row {
    background-color: #ffe6e6 !important;
}

tbody tr.urgent-row td {
    background-color: #ffe6e6 !important;
}

tbody tr.urgent-row:hover {
    background-color: #ffcccc !important;
}

tbody tr.urgent-row:hover td {
    background-color: #ffcccc !important;
}

/* Override any striped table styles */
.table-striped tbody tr.urgent-row:nth-of-type(odd) {
    background-color: #ffe6e6 !important;
}

.table-striped tbody tr.urgent-row:nth-of-type(odd) td {
    background-color: #ffe6e6 !important;
}

.table-striped tbody tr.urgent-row:nth-of-type(even) {
    background-color: #ffe6e6 !important;
}

.table-striped tbody tr.urgent-row:nth-of-type(even) td {
    background-color: #ffe6e6 !important;
}
</style>